import { useState } from "react";
import type { FormEvent } from "react";
import type {
  PayoutStructure,
  PayoutWinner,
  SeasonPayoutWinners,
  Team,
} from "@averagejoesff/shared";
import { upsertPayoutWinners } from "../../api/adminApi";
import { AdminFormStatus } from "../../components/AdminFormStatus";
import { useAdminForm } from "../../hooks/useAdminForm";

interface EditPayoutWinnersFormProps {
  season: number;
  /** This season's roster, read-only here — see `EditWeekForm`'s team selects. */
  teams: Team[];
  structure: PayoutStructure;
  /**
   * Already-loaded winners for this season (or `null` if none saved yet) —
   * the caller must only render this form once its `useResource` call is
   * `"ready"`, so `useState`'s initializer below sees the real data on
   * first mount instead of racing an in-flight fetch (see
   * `EditManagerNamesForm`'s doc comment for why).
   */
  currentWinners: SeasonPayoutWinners | null;
  idToken: string;
  onSaved: (winners: SeasonPayoutWinners) => void;
}

interface Row {
  awardLabel: string;
  teamName: string; // "" means "not yet decided"
}

function toRows(structure: PayoutStructure, currentWinners: SeasonPayoutWinners | null): Row[] {
  const teamByLabel = new Map(currentWinners?.winners.map((w) => [w.awardLabel, w.teamName]) ?? []);
  return structure.awards
    .filter((a) => (a.source ?? "manual") === "manual")
    .map((a) => ({ awardLabel: a.label, teamName: teamByLabel.get(a.label) ?? "" }));
}

function toPayoutWinners(rows: Row[]): PayoutWinner[] {
  return rows
    .filter((r) => r.teamName !== "")
    .map((r) => ({ awardLabel: r.awardLabel, teamName: r.teamName }));
}

/**
 * A fixed row per manually-awarded category in the payout structure — the
 * "Auto"-badged categories (weekly blowout, season standings) aren't
 * editable here at all, since they're resolved straight from the season's
 * computed awards (see `PayoutAwardList`).
 */
export function EditPayoutWinnersForm({
  season,
  teams,
  structure,
  currentWinners,
  idToken,
  onSaved,
}: EditPayoutWinnersFormProps) {
  const [rows, setRows] = useState<Row[]>(() => toRows(structure, currentWinners));
  const { status, error, submit } = useAdminForm(
    () => upsertPayoutWinners(season, toPayoutWinners(rows), idToken),
    onSaved,
  );

  function updateTeam(index: number, teamName: string) {
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, teamName } : r)));
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    void submit();
  }

  if (rows.length === 0) {
    return (
      <p className="admin-panel__hint">
        No manually-awarded categories in the current payout structure — the rest are
        resolved automatically.
      </p>
    );
  }

  if (teams.length === 0) {
    return (
      <p className="admin-panel__hint">
        Add at least one team below before assigning prize winners.
      </p>
    );
  }

  return (
    <form className="admin-panel__form" onSubmit={handleSubmit}>
      <ul className="admin-panel__team-list">
        {rows.map((row, i) => (
          <li key={row.awardLabel} className="admin-panel__team-row">
            <div className="admin-panel__team-row-fields">
              <span className="admin-panel__team-row-label">{row.awardLabel}</span>
              <select value={row.teamName} onChange={(e) => updateTeam(i, e.target.value)}>
                <option value="">— not yet decided —</option>
                {teams.map((t) => (
                  <option key={t.teamKey} value={t.name}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>
          </li>
        ))}
      </ul>

      <div className="admin-panel__actions">
        <button type="submit" disabled={status === "pending"}>
          {status === "pending" ? "Saving…" : "Save prize winners"}
        </button>
        <AdminFormStatus
          status={status}
          error={error}
          savedLabel="Saved — other viewers may take up to a minute to see it."
        />
      </div>
    </form>
  );
}
